import { useEffect, useMemo, useState } from 'react';
import { FileText, Loader2, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';

interface ResultGenerationDialogProps {
  open: boolean;
  status?: string;
  title?: string;
}

const waitingMessages = [
  'Turning the conversation into useful feedback.',
  'Checking the rubric with a steady hand.',
  'Tidying the transcript so the important moments stand out.',
  'Giving the report a quick polish before it meets you.',
  'Nearly there - the feedback is putting on its lab coat.'
];

export function ResultGenerationDialog({
  open,
  status,
  title = 'Generating your results'
}: ResultGenerationDialogProps) {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    if (!open) {
      setMessageIndex(0);
      return;
    }

    const interval = window.setInterval(() => {
      setMessageIndex((current) => (current + 1) % waitingMessages.length);
    }, 3500);

    return () => window.clearInterval(interval);
  }, [open]);

  const displayStatus = useMemo(
    () => status?.trim() || 'Saving the session and preparing your feedback.',
    [status]
  );

  return (
    <Dialog open={open}>
      <DialogContent
        preventOutsideClose
        className="max-w-md overflow-hidden p-0 [&>button]:hidden"
      >
        <div className="bg-gradient-to-br from-primary/10 via-background to-emerald-50 px-6 py-6">
          <DialogHeader className="items-center text-center">
            <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Loader2 className="h-7 w-7 animate-spin" />
            </div>
            <DialogTitle className="text-xl">{title}</DialogTitle>
            <DialogDescription className="max-w-sm">
              You can relax for a moment. We are saving the recording and preparing the feedback.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-5 px-6 pb-6">
          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-start gap-3">
              <div className="mt-0.5 rounded-full bg-primary/10 p-2 text-primary">
                <FileText className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-medium">{displayStatus}</p>
                <p className="mt-1 text-sm text-muted-foreground">{waitingMessages[messageIndex]}</p>
              </div>
            </div>
            <Progress value={72} className="h-2" />
          </div>

          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Sparkles className="h-4 w-4 text-primary" />
            The report is choosing its neatest handwriting.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
